import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const highlights = [
  {
    icon: 'ri-compass-3-line',
    title: '清晰分类导航',
    desc: '按少年、少女、热血、奇幻、科幻等类型划分内容，配合多维筛选快速定位作品。',
  },
  {
    icon: 'ri-play-circle-line',
    title: '专注观看体验',
    desc: '详情页集中展示播放区、剧集列表与资料信息，减少干扰，让追番更专注。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '尊重版权的分享',
    desc: '本站仅作界面演示与学习交流，倡导支持正版，不提供真实视频播放与下载。',
  },
  {
    icon: 'ri-device-line',
    title: '多端友好适配',
    desc: '从手机到桌面均可顺畅浏览，手机端采用单列卡片与抽屉式筛选，轻松触达。',
  },
];

export default function About() {
  useDocumentMeta(
    '关于我们-动漫在线观看',
    '了解动漫在线观看网站的定位、内容范围与联系方式，本站为动漫视频浏览演示站点，倡导尊重版权、支持正版。',
    '关于我们,动漫在线观看,动漫网站介绍,动漫内容范围,免责声明',
  );

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <section className="rounded-lg bg-primary-900 px-6 md:px-12 py-10 md:py-14 mb-10">
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-accent-500 text-background-50 font-semibold mb-4">
          <i className="ri-bookmark-line"></i>
          关于本站
        </span>
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4 max-w-3xl">
          关于动漫在线观看
        </h1>
        <p className="text-sm md:text-base text-primary-100 leading-relaxed max-w-3xl">
          动漫在线观看是一个专注于动漫视频浏览体验的门户站点，面向动画爱好者与二次元文化用户，
          聚合最新与经典动漫作品，通过清晰的分类、多维筛选与贴心的收藏功能，帮助你快速找到想看的作品。
          本站内容均为虚构示例，用于演示网站结构与交互体验。
        </p>
      </section>

      <section className="mb-10">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
          我们提供什么
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {highlights.map((item) => (
            <div key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-5 flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 shrink-0">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-1">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10 rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">内容范围</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-foreground-700 leading-relaxed">
          <div>
            <p className="mb-3">
              本站内容覆盖多种题材类型，包括少年、少女、热血、奇幻、科幻、恋爱、日常与悬疑等，
              并提供地区、年份、热度与评分等多个维度的筛选与排序。
            </p>
            <p>
              列表页与详情页使用的作品标题、封面、简介与剧集信息完全一致的演示数据，
              便于完整体验从浏览到查看详情的流程。
            </p>
          </div>
          <div>
            <p className="mb-3">
              搜索功能支持关键词模糊匹配，可命中作品名称、类型与标签；
              收藏功能基于浏览器本地存储，无需注册登录即可记住你喜欢的作品。
            </p>
            <p>
              站内所有视频播放区域均为前端演示，明确标注“演示模式”，
              不提供真实播放、下载或任何形式的资源分发。
            </p>
          </div>
        </div>
      </section>

      <section className="mb-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-secondary-950 mb-3">联系我们</h2>
          <p className="text-sm text-secondary-900 leading-relaxed mb-5">
            如果你对本站有任何建议、内容反馈或合作意向，欢迎通过邮件与我们联系，
            我们会尽快回复。点击邮箱即可打开你的邮件客户端。
          </p>
          <a
            href="mailto:support@anime-demo.example.com?subject=动漫在线观看-反馈与建议"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-mail-send-line"></i>
            support@anime-demo.example.com
          </a>
          <ul className="mt-5 space-y-2 text-sm text-secondary-900">
            <li className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              在线服务时间：7×24 小时
            </li>
            <li className="flex items-center gap-2">
              <i className="ri-chat-smile-2-line"></i>
              反馈处理：一般 1-2 个工作日内回复
            </li>
          </ul>
        </div>

        <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-3">版权与免责声明</h2>
          <ul className="space-y-3 text-sm text-foreground-700 leading-relaxed">
            <li className="flex gap-2">
              <i className="ri-checkbox-blank-circle-fill text-[8px] mt-2 text-primary-500"></i>
              本站为动漫视频浏览演示站点，站内作品名称、封面与简介均为虚构示例，不对应任何真实作品。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-blank-circle-fill text-[8px] mt-2 text-primary-500"></i>
              本站不存储、不提供任何真实视频资源，播放区域仅为界面演示，不构成侵权或传播行为。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-blank-circle-fill text-[8px] mt-2 text-primary-500"></i>
              我们倡导支持正版内容，如需观看请通过官方授权渠道获取。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-blank-circle-fill text-[8px] mt-2 text-primary-500"></i>
              如对本站内容有异议，欢迎通过上方邮箱联系我们，我们将及时处理。
            </li>
          </ul>
        </div>
      </section>

      <section className="rounded-lg bg-primary-50 border border-primary-200 px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-primary-900">准备好开始追番了吗？</h2>
          <p className="text-sm text-primary-700 mt-1">浏览分类或直接搜索，发现属于你的下一部心动动漫。</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <Link
            to="/category/少年"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-layout-grid-line"></i>
            浏览分类
          </Link>
          <Link
            to="/search"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-50 hover:bg-background-100 text-primary-800 font-medium whitespace-nowrap cursor-pointer border border-primary-300 transition-colors"
          >
            <i className="ri-search-line"></i>
            搜索动漫
          </Link>
        </div>
      </section>
    </div>
  );
}